import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { ExcelExportService } from '../services/excel-export.service';
import {
  ReportType,
  DriverReport,
  TruckReport,
  CustomerReport,
  ConsigneeReport
} from '../models/report.model';

@Component({
  selector: 'app-report',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatCheckboxModule,
    MatInputModule,
    CommonModule,
    MatChipsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatIconModule,
    MatRadioModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatMenuModule,
    MatButtonModule
  ],
  templateUrl: './report.component.html',
  styleUrl: './report.component.scss'
})
export class ReportComponent implements OnInit {
  // Filter properties
  selectedReportType: ReportType = 'driver';
  fromDate: Date | null = null;
  toDate: Date | null = null;
  selectedStatus: string = '';

  // Report type options
  reportTypes = [
    { value: 'driver', label: 'Driver Report' },
    { value: 'truck', label: 'Truck Report' },
    { value: 'customer', label: 'Customer Report' },
    { value: 'consignee', label: 'Consignee Report' }
  ];

  // Status options
  statusOptions = ['All', 'Active', 'Inactive', 'Pending', 'Completed', 'Closed', 'Open'];

  // Data properties
  reportData: any[] = [];
  isLoading: boolean = false;

  // Mock data
  private mockDriverData: DriverReport[] = [
    { driverId: 'DRV001', driverName: 'John Smith', licenseNumber: 'DL-2024-001', phoneNumber: '+1-555-0101', status: 'Active', joinDate: '2023-01-15' },
    { driverId: 'DRV002', driverName: 'Mike Johnson', licenseNumber: 'DL-2024-002', phoneNumber: '+1-555-0102', status: 'Active', joinDate: '2023-03-20' },
    { driverId: 'DRV003', driverName: 'David Brown', licenseNumber: 'DL-2024-003', phoneNumber: '+1-555-0103', status: 'Active', joinDate: '2022-06-10' },
    { driverId: 'DRV004', driverName: 'James Wilson', licenseNumber: 'DL-2024-004', phoneNumber: '+1-555-0104', status: 'Inactive', joinDate: '2023-08-05' },
    { driverId: 'DRV005', driverName: 'Robert Davis', licenseNumber: 'DL-2024-005', phoneNumber: '+1-555-0105', status: 'Pending', joinDate: '2024-01-10' },
  ];

  private mockTruckData: TruckReport[] = [
    { truckId: 'TRK001', plateNumber: 'ABC-1234', model: 'Volvo FH16', capacity: '40 Tons', totalTrips: 250, mileage: 125000, status: 'Active', lastMaintenanceDate: '2024-10-15' },
    { truckId: 'TRK002', plateNumber: 'DEF-5678', model: 'Scania R500', capacity: '35 Tons', totalTrips: 180, mileage: 95000, status: 'Active', lastMaintenanceDate: '2024-11-01' },
    { truckId: 'TRK003', plateNumber: 'GHI-9012', model: 'Mercedes Actros', capacity: '38 Tons', totalTrips: 300, mileage: 180000, status: 'Inactive', lastMaintenanceDate: '2024-09-20' },
    { truckId: 'TRK004', plateNumber: 'JKL-3456', model: 'MAN TGX', capacity: '42 Tons', totalTrips: 150, mileage: 75000, status: 'Active', lastMaintenanceDate: '2024-11-10' },
    { truckId: 'TRK005', plateNumber: 'MNO-7890', model: 'DAF XF', capacity: '36 Tons', totalTrips: 220, mileage: 110000, status: 'Pending', lastMaintenanceDate: '2024-08-25' },
  ];

  private mockCustomerData: CustomerReport[] = [
    { customerId: 'CUS001', customerName: 'Alice Thompson', companyName: 'Global Logistics Inc', email: 'alice@globallogistics.com', phoneNumber: '+1-555-1001', totalOrders: 50, totalRevenue: 125000, status: 'Active', registrationDate: '2022-05-15' },
    { customerId: 'CUS002', customerName: 'Bob Anderson', companyName: 'Fast Freight Co', email: 'bob@fastfreight.com', phoneNumber: '+1-555-1002', totalOrders: 35, totalRevenue: 87500, status: 'Active', registrationDate: '2023-01-20' },
    { customerId: 'CUS003', customerName: 'Carol Martinez', companyName: 'Express Shipping Ltd', email: 'carol@expressship.com', phoneNumber: '+1-555-1003', totalOrders: 75, totalRevenue: 187500, status: 'Active', registrationDate: '2021-11-10' },
    { customerId: 'CUS004', customerName: 'Daniel Lee', companyName: 'Swift Transport', email: 'daniel@swifttrans.com', phoneNumber: '+1-555-1004', totalOrders: 20, totalRevenue: 50000, status: 'Inactive', registrationDate: '2023-06-05' },
    { customerId: 'CUS005', customerName: 'Emma White', companyName: 'Prime Delivery', email: 'emma@primedelivery.com', phoneNumber: '+1-555-1005', totalOrders: 45, totalRevenue: 112500, status: 'Pending', registrationDate: '2024-02-01' },
  ];

  private mockConsigneeData: ConsigneeReport[] = [
    { consigneeId: 'CON001', consigneeName: 'Warehouse Alpha', companyName: 'Alpha Distribution', address: '123 Industrial Ave, New York, NY', phoneNumber: '+1-555-2001', totalDeliveries: 180, status: 'Active', createdDate: '2022-03-15' },
    { consigneeId: 'CON002', consigneeName: 'Beta Logistics Hub', companyName: 'Beta Corp', address: '456 Commerce St, Los Angeles, CA', phoneNumber: '+1-555-2002', totalDeliveries: 150, status: 'Active', createdDate: '2022-07-20' },
    { consigneeId: 'CON003', consigneeName: 'Gamma Storage', companyName: 'Gamma Industries', address: '789 Freight Blvd, Chicago, IL', phoneNumber: '+1-555-2003', totalDeliveries: 220, status: 'Active', createdDate: '2021-12-10' },
    { consigneeId: 'CON004', consigneeName: 'Delta Depot', companyName: 'Delta Supplies', address: '321 Warehouse Rd, Houston, TX', phoneNumber: '+1-555-2004', totalDeliveries: 90, status: 'Inactive', createdDate: '2023-04-05' },
    { consigneeId: 'CON005', consigneeName: 'Epsilon Center', companyName: 'Epsilon Trading', address: '654 Logistics Way, Phoenix, AZ', phoneNumber: '+1-555-2005', totalDeliveries: 120, status: 'Pending', createdDate: '2024-01-15' },
  ];

  // Table columns configuration
  tableColumns: { key: string; label: string }[] = [];

  constructor(
    private router: Router,
    private excelExportService: ExcelExportService
  ) {}

  ngOnInit(): void {
    this.updateTableColumns();
    this.loadReportData();
  }

  onReportTypeChange(): void {
    this.updateTableColumns();
    this.loadReportData();
  }

  updateTableColumns(): void {
    switch (this.selectedReportType) {
      case 'driver':
        this.tableColumns = [
          { key: 'driverId', label: 'Driver ID' },
          { key: 'driverName', label: 'Driver Name' },
          { key: 'licenseNumber', label: 'License Number' },
          { key: 'phoneNumber', label: 'Phone Number' },
          // { key: 'totalTrips', label: 'Total Trips' },
          // { key: 'completedTrips', label: 'Completed Trips' },
          { key: 'status', label: 'Status' },
          { key: 'joinDate', label: 'Join Date' }
        ];
        break;
      case 'truck':
        this.tableColumns = [
          { key: 'truckId', label: 'Truck ID' },
          { key: 'plateNumber', label: 'Plate Number' },
          { key: 'model', label: 'Model' },
          { key: 'capacity', label: 'Capacity' },
          // { key: 'totalTrips', label: 'Total Trips' },
          // { key: 'mileage', label: 'Mileage (km)' },
          { key: 'status', label: 'Status' },
          { key: 'lastMaintenanceDate', label: 'Last Maintenance' }
        ];
        break;
      case 'customer':
        this.tableColumns = [
          { key: 'customerId', label: 'Customer ID' },
          { key: 'customerName', label: 'Customer Name' },
          { key: 'companyName', label: 'Company' },
          { key: 'email', label: 'Email' },
          { key: 'phoneNumber', label: 'Phone' },
          { key: 'totalOrders', label: 'Total Orders' },
          // { key: 'totalRevenue', label: 'Revenue ($)' },
          { key: 'status', label: 'Status' }
        ];
        break;
      case 'consignee':
        this.tableColumns = [
          { key: 'consigneeId', label: 'Consignee ID' },
          { key: 'consigneeName', label: 'Consignee Name' },
          { key: 'companyName', label: 'Company' },
          { key: 'address', label: 'Address' },
          { key: 'phoneNumber', label: 'Phone' },
          { key: 'totalDeliveries', label: 'Total Deliveries' },
          { key: 'status', label: 'Status' }
        ];
        break;
    }
  }

  loadReportData(): void {
    this.isLoading = true;

    // Simulate API call delay
    setTimeout(() => {
      let data: any[] = [];

      switch (this.selectedReportType) {
        case 'driver':
          data = [...this.mockDriverData];
          break;
        case 'truck':
          data = [...this.mockTruckData];
          break;
        case 'customer':
          data = [...this.mockCustomerData];
          break;
        case 'consignee':
          data = [...this.mockConsigneeData];
          break;
      }

      // Apply filters
      this.reportData = this.applyFilters(data);
      this.isLoading = false;
    }, 500);
  }

  applyFilters(data: any[]): any[] {
    let filteredData = [...data];

    // Filter by status
    if (this.selectedStatus && this.selectedStatus !== 'All') {
      filteredData = filteredData.filter(item => item.status === this.selectedStatus);
    }

    // Filter by date range
    if (this.fromDate || this.toDate) {
      filteredData = filteredData.filter(item => {
        const dateKey = this.getDateKeyForReportType();
        const itemDate = new Date(item[dateKey]);

        if (this.fromDate && itemDate < this.fromDate) {
          return false;
        }
        if (this.toDate && itemDate > this.toDate) {
          return false;
        }
        return true;
      });
    }

    return filteredData;
  }

  getDateKeyForReportType(): string {
    switch (this.selectedReportType) {
      case 'driver':
        return 'joinDate';
      case 'truck':
        return 'lastMaintenanceDate';
      case 'customer':
        return 'registrationDate';
      case 'consignee':
        return 'createdDate';
      default:
        return 'createdDate';
    }
  }

  onFilterChange(): void {
    this.loadReportData();
  }

  clearFilters(): void {
    this.fromDate = null;
    this.toDate = null;
    this.selectedStatus = '';
    this.loadReportData();
  }

  downloadReport(format: 'excel' | 'csv'): void {
    if (this.reportData.length === 0) {
      alert('No data to export. Please adjust your filters.');
      return;
    }

    const formattedData = this.excelExportService.formatDataForExport(
      this.reportData,
      this.selectedReportType
    );

    const reportLabel = this.reportTypes.find(r => r.value === this.selectedReportType)?.label || 'Report';
    const dateStr = new Date().toISOString().split('T')[0];
    const fileName = `${reportLabel.replace(' ', '_')}_${dateStr}`;

    if (format === 'excel') {
      this.excelExportService.exportToExcel(formattedData, fileName, reportLabel);
    } else {
      this.excelExportService.exportToCsv(formattedData, fileName);
    }
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'active':
        return 'status-active';
      case 'inactive':
        return 'status-inactive';
      case 'pending':
        return 'status-pending';
      case 'completed':
        return 'status-completed';
      case 'closed':
        return 'status-closed';
      case 'open':
        return 'status-open';
      default:
        return '';
    }
  }

  addNewReport(): void {
    this.router.navigate(['/home/report/register']);
  }
}
